import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Eye,
  Loader2,
  Pencil,
  Plus,
  Tag,
  Trash2,
} from 'lucide-react';
import BookingOfferFormModal from '../../components/MasterAdmin/booking-offers/BookingOfferFormModal';
import BookingOfferViewModal from '../../components/MasterAdmin/booking-offers/BookingOfferViewModal';
import ConfirmDialog from '../../components/MasterAdmin/ui/ConfirmDialog';
import {
  BOOKING_OFFER_SERVICE_OPTIONS,
  BookingOfferConfig,
  deleteBookingOfferConfig,
  fetchBookingOfferConfigs,
  serviceTypeLabel,
  tierSummary,
  toggleBookingOfferActive,
} from '../../services/MasterAdmin/bookingOfferService';
import { fetchDeepLinkRoutes } from '../../services/MasterAdmin/deepLinkService';
import type { DeepLinkRoute } from '../../models/MasterAdmin/deepLink';

const BookingOffersPage: React.FC = () => {
  const [offers, setOffers] = useState<BookingOfferConfig[]>([]);
  const [deepLinkRoutes, setDeepLinkRoutes] = useState<DeepLinkRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingOffer, setEditingOffer] = useState<BookingOfferConfig | null>(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewOffer, setViewOffer] = useState<BookingOfferConfig | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<BookingOfferConfig | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const usedServiceTypes = useMemo(
    () => offers.map((o) => o.serviceType),
    [offers]
  );

  const loadOffers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const rows = await fetchBookingOfferConfigs();
      setOffers(rows);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load offers');
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOffers();
    fetchDeepLinkRoutes(false).then(setDeepLinkRoutes).catch(() => setDeepLinkRoutes([]));
  }, [loadOffers]);

  const openCreate = () => {
    setFormMode('create');
    setEditingOffer(null);
    setFormOpen(true);
  };

  const openEdit = (offer: BookingOfferConfig) => {
    setFormMode('edit');
    setEditingOffer(offer);
    setFormOpen(true);
  };

  const openView = (offer: BookingOfferConfig) => {
    setViewOffer(offer);
    setViewOpen(true);
  };

  const handleToggleActive = async (offer: BookingOfferConfig) => {
    try {
      setActionId(offer.offerConfigId);
      setError(null);
      await toggleBookingOfferActive(offer.serviceType, !offer.isActive);
      setSuccessMessage(
        `Offer ${offer.isActive ? 'deactivated' : 'activated'} successfully.`
      );
      await loadOffers();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update offer status');
    } finally {
      setActionId(null);
    }
  };

  const requestDelete = (offer: BookingOfferConfig) => {
    setDeleteTarget(offer);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    const label = serviceTypeLabel(deleteTarget.serviceType);
    try {
      setDeleteBusy(true);
      setActionId(deleteTarget.offerConfigId);
      setError(null);
      await deleteBookingOfferConfig(deleteTarget.serviceType);
      setSuccessMessage(`${label} offer deleted.`);
      setDeleteTarget(null);
      await loadOffers();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to delete offer');
    } finally {
      setDeleteBusy(false);
      setActionId(null);
    }
  };

  const handleSaved = async () => {
    setSuccessMessage(
      formMode === 'create' ? 'Offer created successfully.' : 'Offer updated successfully.'
    );
    await loadOffers();
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Tag className="mt-1 h-7 w-7 text-emerald-600 dark:text-emerald-400" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Offers and discounts
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Manage tiered booking discounts and app/web popup banners for ambulance and doctor
              consultation. Discount is absorbed from platform commission.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 self-start rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4" />
          Create offer & discount
        </button>
      </div>

      {!loading && usedServiceTypes.length >= BOOKING_OFFER_SERVICE_OPTIONS.length ? (
        <p className="text-sm text-amber-700 dark:text-amber-300">
          Both service types already have an offer. Use Create to update an existing service offer,
          or delete one first to start fresh.
        </p>
      ) : null}

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-900/60 dark:bg-green-900/20 dark:text-green-300">
          {successMessage}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-600 dark:text-gray-300">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading offers…
          </div>
        ) : offers.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <p className="text-gray-600 dark:text-gray-300">No offers created yet.</p>
            <button
              type="button"
              onClick={openCreate}
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
            >
              <Plus className="h-4 w-4" />
              Create your first offer
            </button>
          </div>
        ) : (
          <div className="ma-table-scroll overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/70">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                    Service
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                    Headline
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                    Tiers
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                    Banner
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                    Splash
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                    Updated
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700/80 dark:bg-gray-800">
                {offers.map((offer) => {
                  const busy = actionId === offer.offerConfigId;
                  return (
                    <tr key={offer.offerConfigId}>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                        {serviceTypeLabel(offer.serviceType)}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {offer.marketingTitle || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {tierSummary(offer.tiers)}
                      </td>
                      <td className="px-4 py-3">
                        {offer.popupImageUrl ? (
                          <img
                            src={offer.popupImageUrl}
                            alt=""
                            className="h-10 shrink-0 rounded object-cover"
                            style={{ aspectRatio: '352/170' }}
                          />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {offer.showOnSplash ? (
                          <span className="inline-flex rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-800 dark:bg-sky-900/40 dark:text-sky-300">
                            On splash
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void handleToggleActive(offer)}
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors ${
                            offer.isActive
                              ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                          title={offer.isActive ? 'Click to deactivate' : 'Click to activate'}
                        >
                          {busy ? '…' : offer.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {offer.updatedAt
                          ? new Date(offer.updatedAt).toLocaleString()
                          : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openView(offer)}
                            className="rounded-md p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openEdit(offer)}
                            className="rounded-md p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => requestDelete(offer)}
                            className="rounded-md p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BookingOfferFormModal
        open={formOpen}
        mode={formMode}
        initial={editingOffer}
        usedServiceTypes={usedServiceTypes}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />

      <BookingOfferViewModal
        open={viewOpen}
        offer={viewOffer}
        deepLinkRoutes={deepLinkRoutes}
        onClose={() => {
          setViewOpen(false);
          setViewOffer(null);
        }}
      />

      <ConfirmDialog
        open={deleteTarget != null}
        title="Delete offer?"
        message={
          deleteTarget
            ? `The ${serviceTypeLabel(deleteTarget.serviceType)} offer will be permanently removed. Users will no longer see this discount or popup banner. This cannot be undone.`
            : ''
        }
        tone="danger"
        confirmLabel="Delete"
        busy={deleteBusy}
        onCancel={() => {
          if (!deleteBusy) setDeleteTarget(null);
        }}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
};

export default BookingOffersPage;
